import React, { useState } from 'react'
import {
  Stack,
  Link,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box
} from '@mui/material'
import {
  MoreVert as MoreVertIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as LedgerIcon
} from '@mui/icons-material'
import { LightspeedIcon, XeroIcon } from '../SystemIcons'
import { handleLightspeedLinkClick, handleXeroLinkClick } from '../../utils/externalLinks'
import CreditNoteBadge from './CreditNoteBadge'

interface LightspeedData {
  id: string
  invoiceNumber?: string
  saleLink: string
  ledgerLink?: string
}

interface XeroData {
  id?: string
  reference?: string
  invoiceLink?: string
  type?: string
}

interface InvoiceLinkCellProps {
  lightspeed?: LightspeedData
  xero?: XeroData
  showIcons?: boolean
  iconSize?: number
  layout?: 'vertical' | 'horizontal'
}

const InvoiceLinkCell: React.FC<InvoiceLinkCellProps> = ({
  lightspeed,
  xero,
  showIcons = true,
  iconSize = 16,
  layout = 'vertical'
}) => {
  const [lightspeedAnchorEl, setLightspeedAnchorEl] = useState<HTMLElement | null>(null)
  const [xeroAnchorEl, setXeroAnchorEl] = useState<HTMLElement | null>(null)

  const handleLightspeedMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLightspeedAnchorEl(event.currentTarget)
  }

  const handleLightspeedMenuClose = () => {
    setLightspeedAnchorEl(null)
  }

  const handleViewSaleHistory = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (lightspeed?.saleLink) {
      handleLightspeedLinkClick(
        event,
        lightspeed.saleLink,
        `Sale History ${lightspeed.invoiceNumber || lightspeed.id}`
      )
    }
    handleLightspeedMenuClose()
  }

  const handleViewSaleLedger = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (lightspeed?.ledgerLink) {
      handleLightspeedLinkClick(
        event,
        lightspeed.ledgerLink,
        `Sale Ledger ${lightspeed.invoiceNumber || lightspeed.id}`
      )
    }
    handleLightspeedMenuClose()
  }

  const handleXeroMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setXeroAnchorEl(event.currentTarget)
  }

  const handleXeroMenuClose = () => {
    setXeroAnchorEl(null)
  }

  const handleViewXeroInvoice = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (xero?.invoiceLink) {
      const docType = xero.type === 'credit_note' ? 'Credit Note' : 'Invoice'
      handleXeroLinkClick(
        event,
        xero.invoiceLink,
        `${docType} ${xero.reference || xero.id}`
      )
    }
    handleXeroMenuClose()
  }

  const renderLightspeedSection = () => {
    if (!lightspeed) return null

    const displayText = lightspeed.invoiceNumber || `${lightspeed.id.substring(0, 8)}...`

    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ whiteSpace: 'nowrap' }}>
        {showIcons && <LightspeedIcon size={iconSize} />}
        <Link
          href={lightspeed.saleLink}
          onClick={(e) => handleLightspeedLinkClick(
            e, 
            lightspeed.saleLink, 
            `Sale ${lightspeed.invoiceNumber || lightspeed.id}`
          )}
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
            fontWeight: 500,
            fontSize: '0.875rem'
          }}
        >
          {displayText}
        </Link>
        <IconButton
          size="small"
          onClick={handleLightspeedMenuOpen}
          title="Lightspeed options"
          sx={{ 
            padding: '2px',
            color: 'primary.main',
            '&:hover': { 
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              transform: 'scale(1.05)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Stack>
    )
  }

  const renderXeroSection = () => {
    if (!xero || !xero.invoiceLink) {
      return xero ? (
        <Typography variant="caption" color="text.secondary">
          No Xero link
        </Typography>
      ) : null
    }

    // Use reference if available and not empty, otherwise fall back to truncated ID
    const displayText = xero.reference && xero.reference.trim()
      ? xero.reference
      : xero.id ? `${xero.id.substring(0, 8)}...` : 'N/A'

    const isCreditNote = xero.type === 'credit_note'

    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ whiteSpace: 'nowrap' }}>
        {showIcons && <XeroIcon size={iconSize} />}
        <Link
          href={xero.invoiceLink}
          onClick={(e) => {
            const docType = xero.type === 'credit_note' ? 'Credit Note' : 'Invoice'
            handleXeroLinkClick(
              e, 
              xero.invoiceLink!, 
              `${docType} ${xero.reference || xero.id}`
            )
          }}
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
            fontWeight: 500,
            fontSize: '0.875rem'
          }}
        >
          {displayText}
        </Link>
        {isCreditNote && <CreditNoteBadge />}
        <IconButton
          size="small"
          onClick={handleXeroMenuOpen}
          title="Xero options"
          sx={{ 
            padding: '2px',
            color: '#13A3CD',
            '&:hover': { 
              backgroundColor: 'rgba(19, 163, 205, 0.1)',
              transform: 'scale(1.05)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Stack>
    )
  }

  // If neither system has data, show N/A
  if (!lightspeed && !xero) {
    return (
      <Typography variant="body2" color="textSecondary">
        N/A
      </Typography>
    )
  }

  const lightspeedSection = renderLightspeedSection()
  const xeroSection = renderXeroSection()

  // If only one system has data, render just that in a container
  if (!lightspeedSection && xeroSection) {
    return (
      <Box sx={{ position: 'relative' }}>
        {xeroSection}
        {/* Xero Menu */}
        {xero && xero.invoiceLink && (
          <Menu
            anchorEl={xeroAnchorEl}
            open={Boolean(xeroAnchorEl)}
            onClose={handleXeroMenuClose}
            disablePortal={true}
            disableAutoFocus={true}
            disableEnforceFocus={true}
            disableRestoreFocus={false}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <MenuItem
              component={Link}
              href={xero.invoiceLink}
              onClick={handleViewXeroInvoice}
            >
              <ListItemIcon>
                <XeroIcon size={16} />
              </ListItemIcon>
              <ListItemText>
                View {xero.type === 'credit_note' ? 'credit note' : 'invoice'}
              </ListItemText>
            </MenuItem>
          </Menu>
        )}
      </Box>
    )
  }

  if (lightspeedSection && !xeroSection) {
    return (
      <Box sx={{ position: 'relative' }}>
        {lightspeedSection}
        {/* Lightspeed Menu */}
        {lightspeed && (
          <Menu
            anchorEl={lightspeedAnchorEl}
            open={Boolean(lightspeedAnchorEl)}
            onClose={handleLightspeedMenuClose}
            disablePortal={true}
            disableAutoFocus={true}
            disableEnforceFocus={true}
            disableRestoreFocus={false}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <MenuItem
              component={Link}
              href={lightspeed.saleLink}
              onClick={handleViewSaleHistory}
            >
              <ListItemIcon>
                <ReceiptIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                View sale history
              </ListItemText>
            </MenuItem>
            
            {lightspeed.ledgerLink && (
              <MenuItem
                component={Link}
                href={lightspeed.ledgerLink}
                onClick={handleViewSaleLedger}
              >
                <ListItemIcon>
                  <LedgerIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  View sale ledger
                </ListItemText>
              </MenuItem>
            )}
          </Menu>
        )}
      </Box>
    )
  }

  // Both systems have data - render based on layout
  return (
    <Box sx={{ position: 'relative' }}>
      <Stack 
        direction={layout === 'horizontal' ? 'row' : 'column'} 
        spacing={layout === 'horizontal' ? 2 : 1}
        alignItems={layout === 'horizontal' ? 'center' : 'flex-start'}
      >
        {lightspeedSection}
        {xeroSection}
      </Stack>

      {/* Lightspeed Menu */}
      {lightspeed && (
        <Menu
          anchorEl={lightspeedAnchorEl}
          open={Boolean(lightspeedAnchorEl)}
          onClose={handleLightspeedMenuClose}
          disablePortal={true}
          disableAutoFocus={true}
          disableEnforceFocus={true}
          disableRestoreFocus={false}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem
            component={Link}
            href={lightspeed.saleLink}
            onClick={handleViewSaleHistory}
          >
            <ListItemIcon>
              <ReceiptIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              View sale history
            </ListItemText>
          </MenuItem>
          
          {lightspeed.ledgerLink && (
            <MenuItem
              component={Link}
              href={lightspeed.ledgerLink}
              onClick={handleViewSaleLedger}
            >
              <ListItemIcon>
                <LedgerIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                View sale ledger
              </ListItemText>
            </MenuItem>
          )}
        </Menu>
      )}

      {/* Xero Menu */}
      {xero && xero.invoiceLink && (
        <Menu
          anchorEl={xeroAnchorEl}
          open={Boolean(xeroAnchorEl)}
          onClose={handleXeroMenuClose}
          disablePortal={true}
          disableAutoFocus={true}
          disableEnforceFocus={true}
          disableRestoreFocus={false}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem
            component={Link}
            href={xero.invoiceLink}
            onClick={handleViewXeroInvoice}
          >
            <ListItemIcon>
              <XeroIcon size={16} />
            </ListItemIcon>
            <ListItemText>
              View invoice
            </ListItemText>
          </MenuItem>
        </Menu>
      )}
    </Box>
  )
}

export default InvoiceLinkCell